/* Created by Interactive 63 - https://interactive63.com */
/**
 * Cubic Bézier easing, solved the way browsers do: Newton-Raphson on the x curve with a
 * bisection fallback, then evaluating y at the solved parameter.
 */

export type EasingFunction = (t: number) => number;

/** Control points of the four CSS keyword easings. */
export const KEYWORD_BEZIERS = {
  ease: [0.25, 0.1, 0.25, 1],
  'ease-in': [0.42, 0, 1, 1],
  'ease-out': [0, 0, 0.58, 1],
  'ease-in-out': [0.42, 0, 0.58, 1],
} as const;

const NEWTON_ITERATIONS = 8;
const TOLERANCE = 1e-7;
const MIN_DERIVATIVE = 1e-6;

/** Limits a number to 0..1. */
function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/**
 * Creates an easing function for a cubic Bézier from (0,0) to (1,1). The x control points are
 * clamped to 0..1 so x is monotonic; y control points may overshoot.
 */
export function cubicBezier(x1: number, y1: number, x2: number, y2: number): EasingFunction {
  const px1 = clamp01(x1);
  const px2 = clamp01(x2);

  // Polynomial coefficients of the curve in the form ((a*s + b)*s + c)*s.
  const cx = 3 * px1;
  const bx = 3 * (px2 - px1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;

  /** x position of the curve at parameter s. */
  const sampleX = (s: number): number => ((ax * s + bx) * s + cx) * s;
  /** y position of the curve at parameter s. */
  const sampleY = (s: number): number => ((ay * s + by) * s + cy) * s;
  /** Derivative of x with respect to s. */
  const slopeX = (s: number): number => (3 * ax * s + 2 * bx) * s + cx;

  /** Finds the parameter s where x(s) equals the target. */
  const solve = (target: number): number => {
    let s = target;
    for (let i = 0; i < NEWTON_ITERATIONS; i += 1) {
      const error = sampleX(s) - target;
      if (Math.abs(error) < TOLERANCE) return s;
      const slope = slopeX(s);
      if (Math.abs(slope) < MIN_DERIVATIVE) break;
      s -= error / slope;
    }

    let low = 0;
    let high = 1;
    s = target;
    for (let i = 0; i < 64; i += 1) {
      const error = sampleX(s) - target;
      if (Math.abs(error) < TOLERANCE) break;
      if (error > 0) high = s;
      else low = s;
      s = (low + high) / 2;
    }
    return s;
  };

  return (t) => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    return sampleY(solve(t));
  };
}
